import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { TailSpin } from "react-loader-spinner";

function formatPaise(paise) {
  const value = Number(paise || 0) / 100;
  return `₹${value.toFixed(0)}`;
}

function formatServeType(serveType) {
  if (!serveType) return "-";
  if (serveType === "DINE_IN") return "Dine in";
  if (serveType === "TAKEAWAY") return "Takeaway";
  if (serveType === "DELIVERY") return "Delivery";
  if (serveType === "RECEIVE_IN_BUS") return "Receive in Bus";
  return serveType;
}

function OrderSummary() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [redirectionUrl, setRedirectionUrl] = useState(null);
  const [redirectionMessage, setRedirectionMessage] = useState(null);

  const goToRedirectionUrl = () => {
    if (redirectionUrl) {
      window.location.replace(redirectionUrl);
      return;
    }
    // last-resort fallback (should be rare if checkout always passes redirectionUrl)
    window.location.replace("https://tavahot.com");
  };

  useEffect(() => {
    const fetchSummary = async () => {
      if (!orderId) {
        setError("Missing order id");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(`/api/nexus/order/summary/${orderId}/v3`, {
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
          },
        });
        setSummary(response.data);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Error fetching order summary:", e);
        setError("Failed to load order summary. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [orderId]);

  // Capture redirectionUrl from navigation state, with sessionStorage fallback for reloads.
  useEffect(() => {
    const stateUrl = location?.state?.redirectionUrl;
    const stateMsg = location?.state?.redirectionMessage;
    if (stateUrl || stateMsg) {
      if (stateUrl) setRedirectionUrl(stateUrl);
      if (stateMsg) setRedirectionMessage(stateMsg);
      try {
        if (orderId) {
          sessionStorage.setItem(
            `orderRedirect:${orderId}`,
            JSON.stringify({ redirectionUrl: stateUrl, redirectionMessage: stateMsg })
          );
        }
      } catch (e) {
        // ignore
      }
      return;
    }

    try {
      if (orderId) {
        const stored = sessionStorage.getItem(`orderRedirect:${orderId}`);
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            if (parsed?.redirectionUrl) setRedirectionUrl(parsed.redirectionUrl);
            if (parsed?.redirectionMessage) setRedirectionMessage(parsed.redirectionMessage);
          } catch (e) {
            // backward compatibility: stored as raw url string
            setRedirectionUrl(stored);
          }
        }
      }
    } catch (e) {
      // ignore
    }
  }, [location?.state, orderId]);

  // Clear navigation stack / block going back to Checkout.
  // If back is pressed, send user to the redirectionUrl.
  useEffect(() => {
    try {
      window.history.pushState({ orderSummary: true }, "", window.location.href);
    } catch (e) {
      // ignore
    }

    const onPopState = () => {
      goToRedirectionUrl();
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectionUrl]);
  const breakup = summary?.paymentBreakup;
  const items = useMemo(() => breakup?.items || [], [breakup]);
  const restaurantLat = summary?.restaurantLocation?.lat;
  const restaurantLng = summary?.restaurantLocation?.lng;
  const mapsUrl =
    restaurantLat && restaurantLng
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          `${restaurantLat},${restaurantLng}`
        )}`
      : null;

  if (loading) {
    return (
      <div className="pt-24 flex flex-col w-full h-full items-center bg-[#FBFCFF]">
        <div className="flex justify-center items-center">
          <TailSpin
            height="80"
            width="80"
            color="#FBC02D"
            ariaLabel="loading"
            radius="1"
            visible={true}
          />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pt-24 flex flex-col w-full h-full items-center bg-[#FBFCFF] px-4">
        <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-semibold text-red-600 mb-2">Order Summary</h2>
          <p className="text-gray-700">{error}</p>
          <div className="flex gap-3 mt-4">
            <button
              onClick={goToRedirectionUrl}
              className="flex-1 bg-gray-100 text-[#171725] py-3 rounded-lg font-semibold"
            >
              Back
            </button>
            <button
              onClick={() => window.location.reload()}
              className="flex-1 bg-[#FBC02D] text-white py-3 rounded-lg font-semibold"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 flex flex-col w-full h-full items-center bg-[#FBFCFF] pb-32">
      <div className="w-full h-full md:w-full overflow-y-auto">
        <div className="mb-2 flex justify-center">
          <h1 className="text-2xl md:text-3xl font-semibold">Order Summary</h1>
        </div>

        <div className="px-2 md:px-5 lg:px-5">
          <div className="mt-4">
            <h2 className="text-xl md:text-2xl font-semibold text-[#171725] pb-3">Items</h2>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              {items.length === 0 ? (
                <p className="text-sm text-[#696974]">No items found.</p>
              ) : (
                items.map((item, idx) => (
                  <div key={`${item.dishId || idx}`} className="flex justify-between py-2">
                    <div className="pr-3">
                      <p className="font-semibold text-[#171725]">
                        {item.dishName} <span className="text-[#FBC02D]">x{item.quantity}</span>
                      </p>
                      {item.foodPreference ? (
                        <p className="text-xs text-[#696974]">
                          {item.foodPreference === "VEG" ? "Veg" : "Non-veg"}
                        </p>
                      ) : null}
                    </div>
                    <p className="font-semibold text-[#171725]">{formatPaise(item.price)}</p>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="mt-4">
            <h2 className="text-xl md:text-2xl font-semibold text-[#171725] pb-3">Price Breakdown</h2>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex justify-between pb-2">
                <p>Total item price</p>
                <p>{formatPaise(breakup?.basePrice)}</p>
              </div>
              <div className="flex justify-between pb-2 text-green-600 font-semibold">
                <p>Discount</p>
                <p>-{formatPaise(breakup?.discount)}</p>
              </div>
              <div className="flex justify-between pb-2">
                <p>Convenience fee</p>
                <p>{formatPaise(breakup?.convenienceFee)}</p>
              </div>
              <div className="flex justify-between pb-2">
                <p>GST</p>
                <p>{formatPaise(breakup?.gst)}</p>
              </div>
              <div className="flex justify-between pb-2">
                <p>Coins used</p>
                <p>-{formatPaise(breakup?.usedCoinAmount)}</p>
              </div>
              <div className="flex justify-between font-bold pt-2 border-t border-gray-200">
                <p className="text-lg md:text-xl">Total</p>
                <p className="text-lg md:text-xl">{formatPaise(breakup?.total)}</p>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <h2 className="text-xl md:text-2xl font-semibold text-[#171725] pb-3">Restaurant & Order Details</h2>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex justify-between items-start gap-3">
                <div>
                  <p className="text-sm text-[#696974]">Restaurant</p>
                  <p className="font-semibold text-[#171725]">{summary?.restaurantName || "-"}</p>
                  <p className="text-sm text-[#696974] mt-1">{summary?.restaurantAddress || ""}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-[#696974]">Order</p>
                  <p className="font-semibold text-[#171725]">
                    {summary?.orderNumber ? `#${summary.orderNumber}` : "-"}
                  </p>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="bg-[#FBFCFF] rounded-lg p-3 col-span-2 md:col-span-3">
                  <p className="text-xs text-[#696974]">Order ID</p>
                  <p className="font-semibold text-[#171725] break-all">{summary?.orderId || orderId || "-"}</p>
                </div>
                <div className="bg-[#FBFCFF] rounded-lg p-3">
                  <p className="text-xs text-[#696974]">Order state</p>
                  <p className="font-semibold text-[#171725]">
                    {summary?.readableOrderState || summary?.orderState || "-"}
                  </p>
                </div>
                <div className="bg-[#FBFCFF] rounded-lg p-3">
                  <p className="text-xs text-[#696974]">Serve type</p>
                  <p className="font-semibold text-[#171725]">{formatServeType(summary?.serveType)}</p>
                </div>
                <div className="bg-[#FBFCFF] rounded-lg p-3">
                  <p className="text-xs text-[#696974]">Restaurant phone</p>
                  {summary?.restaurantPhone ? (
                    <a
                      href={`tel:${summary.restaurantPhone}`}
                      className="font-semibold text-[#171725] underline underline-offset-2"
                    >
                      {summary.restaurantPhone}
                    </a>
                  ) : (
                    <p className="font-semibold text-[#171725]">-</p>
                  )}
                </div>
                <div className="bg-[#FBFCFF] rounded-lg p-3">
                  <p className="text-xs text-[#696974]">Location</p>
                  {mapsUrl ? (
                    <a
                      href={mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 font-semibold text-[#171725] underline underline-offset-2"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path
                          d="M12 22s7-4.5 7-12a7 7 0 1 0-14 0c0 7.5 7 12 7 12Z"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                        <path
                          d="M12 13.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                      </svg>
                      View on map
                    </a>
                  ) : (
                    <p className="font-semibold text-[#171725]">-</p>
                  )}
                </div>
                <div className="bg-[#FBFCFF] rounded-lg p-3 col-span-2 md:col-span-3">
                  <p className="text-xs text-[#696974]">Pay mode</p>
                  <p className="font-semibold text-[#171725]">Offline (Pay at counter)</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-10">
        <div className="w-full md:w-[40%] mx-auto flex flex-col gap-2">
          <button
            onClick={goToRedirectionUrl}
            className="w-full bg-[#FBC02D] rounded-2xl py-3 text-lg font-semibold"
          >
            {redirectionMessage || "Continue"}
          </button>
          <p className="text-center text-sm text-[#696974]">
            Show this summary at the counter to pay offline.
          </p>
        </div>
      </div>
    </div>
  );
}

export default OrderSummary;


